/** React libraries */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AppRouter } from './routes/AppRouter';
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { ToastContainer } from 'react-toastify';

/** Components, Utitlity, Types, Constants */
import { reactPlugin } from './utils/appInsights';
import { DEFAULT_ERROR_MSG, DEFAULT_LANGUAGE, WEGLOT_KEY } from './utils/constants';
import { LoaderProvider } from "./context/LoaderContext";

/** Style */
import './App.css';
import "../src/assets/css/colors.css";
import "../src/assets/css/styles.css";
import "../src/assets/js/main.js"
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../src/assets/css/style.min.css';
import '../src/assets/css/fonts.css';

// Custom hook to trigger Weglot on route change
const useWeglotOnRouteChange = () => {
  const location = useLocation();

  useEffect(() => {
    // Give React time to render new page before refreshing translation
    const timer = setTimeout(() => {
      if (window.Weglot) {
        const current = window.Weglot.getCurrentLang();
        window.Weglot.switchTo(current);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);
};


function App() {
  const [lang, setLang] = useState<string | null>(null);
  const [isWeglotReady, setIsWeglotReady] = useState(false);
  useWeglotOnRouteChange();

  const getLanguage = (): string => {
    try {
      const storedLanguage = localStorage.getItem("selectedLang");
      return storedLanguage || DEFAULT_LANGUAGE;
    } catch (error) {
      return DEFAULT_LANGUAGE;
    }
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.weglot.com/weglot.min.js";
    script.async = true;

    script.onload = () => {
      if (window.Weglot) {
        // Add delay before initializing
        const delay = 10;

        const timer = setTimeout(() => {
          window.Weglot.initialize({
            api_key: WEGLOT_KEY,
            originalLanguage: getLanguage(),
            destinationLanguages: ["es", "nl", "sv"],
            excludedBlocks: [".no-translate"]
          });

          window.Weglot.on("initialized", () => {
            setIsWeglotReady(true);

            const storedLanguage = getLanguage();
            if (storedLanguage && storedLanguage !== DEFAULT_LANGUAGE) {
              window.Weglot.switchTo(storedLanguage);
            }
          });

          window.Weglot.on("languageChanged", (newLang: string) => {
            //console.log("Language changed →", newLang);
            localStorage.setItem("selectedLang", newLang);
            setLang(newLang);
          });
        }, delay);

        // cleanup
        return () => clearTimeout(timer);
      }
    };

    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  

  useEffect(() => {
    const interval = setInterval(() => {
      const storedLanguage = localStorage.getItem("selectedLang");
      if (storedLanguage) {
        setLang(storedLanguage);
        clearInterval(interval);
      }
    }, 1000); // check every second
    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    if (!lang || !isWeglotReady || !window.Weglot) return;
    try {
      const userLang = getLanguage();
      const current = window.Weglot.getCurrentLang();

      if (current !== userLang) {
        //console.log("Switching Weglot to user language:", userLang);
        window.Weglot.switchTo(userLang);
        localStorage.setItem("selectedLang", userLang);
      }

    } catch (err) {
      console.error("Weglot switch error:", err);
    }
  }, [lang, isWeglotReady]);

  return (
    <>
      <AppInsightsErrorBoundary onError={() => <h1>{DEFAULT_ERROR_MSG}</h1>} appInsights={reactPlugin}>
        <>
          <LoaderProvider>
            <AppRouter />
          </LoaderProvider>
          <ToastContainer position="top-right" autoClose={3000} />
        </>
      </AppInsightsErrorBoundary>
    </>
  );
}

export default App;
